import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth, useGiftCardsData, useRedemption } from '../../hooks/useApi';
import { animations } from '../../styles/theme';

const RedemptionContainer = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: 2rem auto;
  padding: 2rem;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 1024px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem auto;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin: 0.5rem auto;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 215, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 140, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(138, 43, 226, 0.1) 0%, transparent 50%);
   
    pointer-events: none;
  }
      &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);

    
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${animations.sparkle} 4s ease-in-out infinite;

  }
`;

const UserInfo = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
    margin-bottom: 1rem;
  }
`;

const GiftCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const GiftCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  width: 100%;
  
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
   
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
   
    padding: 1rem;
  }
`;

const GiftCardTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
  font-size: clamp(1.2rem, 2.5vw, 1.5rem);
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
  }
`;

const GiftCardDescription = styled.p`
  color: #666;
  margin-bottom: 1rem;
  font-size: clamp(0.9rem, 2vw, 1rem);
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const PointsRequired = styled.div`
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  font-weight: bold;
  color: #007bff;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const RedeemButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: ${props => props.disabled ? '#6c757d' : '#28a745'};
  color: white;
  border: none;
  border-radius: 4px;
  min-height: 44px;
  box-sizing: border-box;
  font-size: clamp(0.9rem, 2vw, 1rem);
  
  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.9rem;
  }
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover:not(:disabled) {
    background-color: #218838;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: center;
`;

const RefreshButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 1rem;
  
  &:hover {
    background: #0056b3;
  }
`;

const GiftCardRedemption = ({ onNavigate }) => {
  const { user, updateUserPoints } = useAuth();
  const { data, loading, error, refetch } = useGiftCardsData(user);
  const { redeeming, redeemGiftCard } = useRedemption();
  const [redemptionMessage, setRedemptionMessage] = useState('');
  const [redemptionSuccess, setRedemptionSuccess] = useState(false);

  const handleRedemption = async (giftCard) => {
    try {
      setRedemptionMessage('');
      setRedemptionSuccess(false);

      const response = await redeemGiftCard(
        giftCard.id,
        1 // quantity
      );

      if (response && response.success) {
        setRedemptionSuccess(true);
        
        // Build success message without redemption codes
        let message = `Successfully redeemed "${giftCard.displayName || giftCard.productName}"! Check your inventory to reveal your code.`;
        
        setRedemptionMessage(message);
        
        // Update user points
        if (response.newTotalPoints !== undefined) {
          updateUserPoints(response.newTotalPoints);
        }
        
        // Refresh data to update available gift cards and user balance
        setTimeout(() => refetch(), 1000);
      } else {
        throw new Error(response?.message || 'Redemption failed');
      }
    } catch (error) {
      console.error('Redemption error:', error);
      setRedemptionSuccess(false);
      setRedemptionMessage(error.message || 'Failed to redeem gift card. Please try again.');
    }
  };

  const clearMessage = () => {
    setRedemptionMessage('');
    setRedemptionSuccess(false);
  };

  if (loading) {
    return (
      <RedemptionContainer>
        <LoadingMessage>Loading gift cards...</LoadingMessage>
      </RedemptionContainer>
    );
  }

  if (error) {
    return (
      <RedemptionContainer>
        <ErrorMessage>
          Error loading data: {error}
          <RefreshButton onClick={refetch}>Try Again</RefreshButton>
        </ErrorMessage>
      </RedemptionContainer>
    );
  }

  if (!data || !data.userData) {
    return (
      <RedemptionContainer>
        <ErrorMessage>No data available</ErrorMessage>
      </RedemptionContainer>
    );
  }

  const { userData, giftCards } = data;

  return (
    <RedemptionContainer>
      <h1>Gift Card Redemption</h1>
      
      <UserInfo>
        <h2>Welcome, {userData.username}!</h2>
        <p><strong>Available Balance:</strong> {userData.points || 0} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /></p>
        <RefreshButton onClick={refetch}>Refresh Data</RefreshButton>
      </UserInfo>

      {redemptionMessage && (
        <>
          {redemptionSuccess ? (
            <SuccessMessage>
              {redemptionMessage}
              <RefreshButton onClick={clearMessage} style={{ marginLeft: '1rem' }}>
                Clear
              </RefreshButton>
            </SuccessMessage>
          ) : (
            <ErrorMessage>
              {redemptionMessage}
              <RefreshButton onClick={clearMessage} style={{ marginLeft: '1rem' }}>
                Clear
              </RefreshButton>
            </ErrorMessage>
          )}
        </>
      )}

      {giftCards && giftCards.length > 0 ? (
        <>
          <h2>Available Gift Cards</h2>
          <GiftCardsGrid>
            {giftCards.map((giftCard) => {
              // Wupex products use sellingPrice (in coins) instead of pointsRequired
              const pointsRequired = giftCard.sellingPrice || giftCard.price || 0;
              const canRedeem = userData.points >= pointsRequired;
              const displayName = giftCard.displayName || giftCard.productName || giftCard.merchantName || 'Gift Card';
              const faceValue = giftCard.price || 0; // Face value in dollars
              
              return (
                <GiftCard key={giftCard.id}>
                  <GiftCardTitle>{displayName}</GiftCardTitle>
                  <GiftCardDescription>
                    {giftCard.merchantName || giftCard.productType || giftCard.category || 'Gift Card'}
                    {faceValue > 0 && ` - $${faceValue} Value`}
                  </GiftCardDescription>
                  <PointsRequired>
                    {pointsRequired.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /> required
                  </PointsRequired>
                  <RedeemButton
                    disabled={!canRedeem || redeeming}
                    onClick={() => handleRedemption(giftCard)}
                  >
                    {redeeming ? 'Redeeming...' : 
                     canRedeem ? 'Redeem Now' : 'Not Enough Balance'}
                  </RedeemButton>
                </GiftCard>
              );
            })}
          </GiftCardsGrid>
        </>
      ) : (
        <LoadingMessage>No gift cards available at the moment.</LoadingMessage>
      )}
    </RedemptionContainer>
  );
};

export default GiftCardRedemption;